import { httpBatchLink, type TRPCLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import { observable } from '@trpc/server/observable';
import superjson from 'superjson';
import { appErrorCode, csrfHeaders } from './api';
import type { AppRouter } from './app-router';
import { offlineLink } from './offline';
import { stepUpKindOf, stepUpStore } from './step-up';

/**
 * tRPC (`/api/trpc`, superjson, nagłówek `x-fos-csrf` z ciasteczka `fos_csrf`, ciasteczka same-origin).
 * Użycie w komponentach: `trpc.identity.listSessions.useQuery()`.
 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * Globalna obsługa STEP_UP_REQUIRED: link wstrzymuje wynik, pokazuje modal („Potwierdź hasłem” albo kod z maila —
 * `error.data.details.kind`) i po potwierdzeniu ponawia tę samą operację. Wywołujący dostaje wynik ponowionej próby.
 * Operacja z `context: { skipStepUp: true }` dostaje błąd wprost (własne okno, np. podgląd hasła z sejfu).
 */
export const stepUpLink: TRPCLink<AppRouter> = () => {
  return ({ op, next }) =>
    observable((observer) => {
      let unsubscribe: (() => void) | null = null;
      let closed = false;
      const attempt = (retried: boolean) => {
        const subscription = next(op).subscribe({
          next: (value) => observer.next(value),
          error: (err) => {
            if (
              !retried &&
              op.type !== 'subscription' &&
              !op.context?.skipStepUp &&
              appErrorCode(err) === 'STEP_UP_REQUIRED'
            ) {
              void stepUpStore.request(stepUpKindOf(err)).then((confirmed) => {
                if (closed) return;
                if (confirmed) attempt(true);
                else observer.error(err);
              });
              return;
            }
            observer.error(err);
          },
          complete: () => observer.complete(),
        });
        unsubscribe = () => subscription.unsubscribe();
      };
      attempt(false);
      return () => {
        closed = true;
        unsubscribe?.();
      };
    });
};

/** Wymienny link końcowy (tryb dev ?mock=1 podmienia go na atrapę, patrz lib/mock). */
let terminatingLink: TRPCLink<AppRouter> | null = null;

export function setTrpcTerminatingLink(link: TRPCLink<AppRouter> | null): void {
  terminatingLink = link;
}

export function createTrpcClient() {
  return trpc.createClient({
    links: [
      // offline: mutacja odrzucona od razu, bez kolejki (lib/offline.ts)
      offlineLink,
      stepUpLink,
      terminatingLink ??
        httpBatchLink({
          url: '/api/trpc',
          transformer: superjson,
          headers: () => csrfHeaders(),
          fetch: (url, init) => fetch(url, { ...init, credentials: 'same-origin' }),
        }),
    ],
  });
}
